import * as React from "react";
import { useState, useCallback } from "react";
import { OrderedSet } from "immutable";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import DescriptionBox from "views/components/aggregationbuilder/DescriptionBox";
import Input from "components/bootstrap/Input";
import StickyBottomActions from "views/components/widgets/StickyBottomActions";
import SaveOrCancelButtons from "views/components/widgets/SaveOrCancelButtons";
import FieldsConfiguration from "views/components/widgets/FieldsConfiguration";
import FieldSelect from "views/components/aggregationwizard/FieldSelect";
const Container = styled.div`
  height: 100%;
  width: 100%;
  padding-bottom: 15px;
  display: flex;
`;
const Col = styled.div`
  height: 100%;
`;
const ControlsCol = styled(Col)`
  min-width: 315px;
  max-width: 500px;
  flex: 1.2;
  padding-right: 5px;
  margin-right: 10px;
`;
const LogViewCol = styled(Col)`
  flex: 3;
  overflow-y: hidden;
`;
const Warning = styled.div(
  ({ theme }) => css`
    padding: 5px;
    border: 1px solid ${theme.colors.variant.warning};
    border-radius: 3px;
    background: ${theme.colors.variant.lightest.warning};
    color: ${theme.utils.contrastingColor(theme.colors.variant.lightest.warning)};
  `
);
const LogViewEditWidget = ({ config, onChange, children, onCancel }) => {
  const [currentConfig, setCurrentConfig] = useState(config);
  const { t } = useTranslation("enterprise_search");
  const setTieBreaker = useCallback(
    (value) => {
      const tieBreaker = value && value.length > 0 ? value : null;
      const newConfig = currentConfig.toBuilder().tieBreaker(tieBreaker).build();
      setCurrentConfig(newConfig);
      onChange(newConfig);
    },
    [currentConfig, onChange]
  );
  const updateFields = useCallback(
    (fields) => {
      const newConfig = currentConfig.toBuilder().fields(OrderedSet(fields)).build();
      setCurrentConfig(newConfig);
      onChange(newConfig);
    },
    [currentConfig, onChange]
  );
  const changeSortDirection = useCallback(
    (direction) => {
      const newConfig = currentConfig.toBuilder().sort(direction).build();
      setCurrentConfig(newConfig);
      onChange(newConfig);
    },
    [currentConfig, onChange]
  );
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(ControlsCol, null, /* @__PURE__ */ React.createElement(StickyBottomActions, { actions: /* @__PURE__ */ React.createElement(SaveOrCancelButtons, { onCancel }), alignActionsAtBottom: true }, /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("log_view_edit_widget.sort_direction") }, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "radio",
      id: "sort-asc",
      name: "sort",
      value: "asc",
      label: t("log_view_edit_widget.ascending"),
      onChange: () => changeSortDirection("ASC"),
      checked: currentConfig.sort === "ASC"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "radio",
      id: "sort-desc",
      name: "sort",
      value: "desc",
      label: t("log_view_edit_widget.descending"),
      onChange: () => changeSortDirection("DESC"),
      checked: currentConfig.sort === "DESC"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "tie-breaker",
      name: "tie-breaker",
      help: t("log_view_edit_widget.tie_breaker_help"),
      label: t("log_view_edit_widget.tie_breaker")
    },
    /* @__PURE__ */ React.createElement(
      FieldSelect,
      {
        id: "field-create-select",
        onChange: setTieBreaker,
        name: "tie-breaker",
        excludedFields: ["timestamp"],
        value: currentConfig.tieBreaker,
        placeholder: t("log_view_edit_widget.select_tie_breaker"),
        ariaLabel: t("log_view_edit_widget.select_tie_breaker")
      }
    )
  )), /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("log_view_edit_widget.field_selection_and_order") }, /* @__PURE__ */ React.createElement(FieldsConfiguration, { onChange: updateFields, selectSize: "normal", selectedFields: config.fields.toArray() }), currentConfig.fields.size <= 0 && /* @__PURE__ */ React.createElement(Warning, null, t("log_view_edit_widget.no_fields_selected"))))), /* @__PURE__ */ React.createElement(LogViewCol, null, children));
};
export default LogViewEditWidget;
